/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Loads the inventory lots of each catalog part on an Expense Inventory case, so a catalog line's
 * Lot/Serial field can suggest them. Uses the query and the account rules Add Parts uses (the
 * rep's IT accounts; IT and IH in Ops review), one call per distinct catalog part.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/hook/useExpenseInventoryCatalogLotOptions
 */
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { GET_SUBINVENTORY_LIST_WITH_LOT_DETAILS } from '../../../../../../graphql/getSubInventoryWithLotDetails';
import { USAGE_TYPE } from '../../../../../../util/constants';
import { INVENTORY_CHANNELS } from '../../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/useParts';
import { CatalogLotInventoryRow } from '../samplingLineRules';

interface UseExpenseInventoryCatalogLotOptionsProps {
  expenseInventoryDetail: any;
  isReview: boolean;
  skip: boolean;
}

const PRODUCT_NUMBER_KEY_SEPARATOR = '|';

const useExpenseInventoryCatalogLotOptions = ({
  expenseInventoryDetail,
  isReview,
  skip,
}: UseExpenseInventoryCatalogLotOptionsProps): {
  catalogLotRowsByProductNumber: Record<string, CatalogLotInventoryRow[]>;
} => {
  const apolloClient = useApolloClient();
  const lineItems = expenseInventoryDetail?.lineItems;
  const subInventoryCode = expenseInventoryDetail?.subInventoryCode;
  const salesRepId = expenseInventoryDetail?.salesRepId;
  const [catalogLotRowsByProductNumber, setCatalogLotRowsByProductNumber] = useState<
    Record<string, CatalogLotInventoryRow[]>
  >({});
  const catalogProductNumbersKey = useMemo(
    () =>
      Array.from(
        new Set<string>(
          (lineItems || [])
            .filter((line: any) => line?.sourceLocation === INVENTORY_CHANNELS.CATALOG)
            .map((line: any) => line?.productNumber)
            .filter(Boolean)
        )
      )
        .sort()
        .join(PRODUCT_NUMBER_KEY_SEPARATOR),
    [lineItems]
  );

  useEffect(() => {
    if (skip || !catalogProductNumbersKey) {
      return undefined;
    }
    let isCurrent = true;
    const loadCatalogLotRows = async (): Promise<void> => {
      const entries = await Promise.all(
        catalogProductNumbersKey.split(PRODUCT_NUMBER_KEY_SEPARATOR).map(async productNumber => {
          try {
            const { data } = await apolloClient.query({
              query: GET_SUBINVENTORY_LIST_WITH_LOT_DETAILS,
              variables: {
                subInventoryCode,
                salesRepId,
                recordType: USAGE_TYPE.EXPENSE_INVENTORY,
                isReview,
                tabName: INVENTORY_CHANNELS.REPSTOCK,
                filters: { productNumber },
              },
              fetchPolicy: 'no-cache',
            });
            return { productNumber, rows: data?.getSubInventoryWithLotDetails || [] };
          } catch {
            // Without lots the field is a plain text box, as it is for a part the rep doesn't hold.
            return { productNumber, rows: [] };
          }
        })
      );
      if (isCurrent) {
        setCatalogLotRowsByProductNumber(
          entries.reduce<Record<string, CatalogLotInventoryRow[]>>(
            (rowsByProduct, entry) => ({ ...rowsByProduct, [entry.productNumber]: entry.rows }),
            {}
          )
        );
      }
    };
    loadCatalogLotRows();
    return (): void => {
      isCurrent = false;
    };
  }, [
    apolloClient,
    catalogProductNumbersKey,
    // Every save refetches the detail, so the available quantities reload after each save.
    lineItems,
    subInventoryCode,
    salesRepId,
    isReview,
    skip,
  ]);

  return { catalogLotRowsByProductNumber };
};

export default useExpenseInventoryCatalogLotOptions;
